/**
 * Art — every Lands illustration ships as this (design-handoff/rc_lands_shared.jsx `Art`).
 *
 * Resolves an asset id from `ASSETS.md` to `/lands/<group>/<id>@2x.png`. While the
 * image pipeline has not delivered a file, the labeled placeholder renders instead:
 * a soft block in a palette tint with the id on it (requirements N9 — never hand-drawn
 * art). An `<img>` that fails to load drops back to the placeholder, so files can land
 * one at a time.
 */
import { useState, type CSSProperties, type ReactNode } from "react";

export const TINT: Record<string, string> = {
  grass: "var(--color-grass)",
  sky: "var(--color-sky)",
  sand: "var(--color-sand)",
  clay: "var(--color-clay)",
  lavender: "var(--color-lavender)",
  rose: "var(--color-rose)",
  coral: "var(--color-coral)",
  sun: "var(--color-sun)",
  ink: "var(--color-ink)",
  base: "var(--color-base-muted)",
  sea: "color-mix(in srgb, var(--color-sky) 70%, var(--color-ink))",
  stone: "color-mix(in srgb, var(--color-base-muted) 55%, var(--color-ink-muted))",
  night: "color-mix(in srgb, var(--color-lavender) 45%, var(--color-ink))",
};

/**
 * Folder per family, under the client's own `public/lands/`. These are the assets the DOM
 * and the Phaser scene fetch at runtime, as opposed to the ones that arrive as building
 * template records with a published image (landmarks, ground tiles, placed objects — see
 * `ART_DELIVERY_RUNBOOK.md` §5). Delivery for anything resolved here is "put the file at
 * this path and it ships with the client bundle".
 *
 * Keyed on the first segment, so a new family lands in its own folder rather than
 * silently falling through to `bg` the way `lookout.*`, `placed.*` and `prop.*` did
 * before 2026-10-16.
 */
const ASSET_FOLDERS: Record<string, string> = {
  ui: "ui",
  pet: "actors",
  resident: "actors",
  avatar: "actors",
  scout: "actors",
  map: "map",
  seed: "seeds",
  lookout: "lookout",
};

/**
 * Where `/lands/...` is served from. Empty (the default, and prod) means the page's own
 * origin: `worldby.me` sits behind the distribution whose `/lands/*` behavior reads the
 * web-content bucket (`infra/cloudformation.yml`). `dev.worldby.me` and a local Vite
 * server are not behind it, so their builds set `VITE_LANDS_ASSET_BASE` to the origin
 * that is (ART_DELIVERY_RUNBOOK.md §8). Exported for the one test that pins the shape.
 */
export function landsAssetBase(
  env: Record<string, string | undefined> = import.meta.env as Record<string, string | undefined>,
): string {
  return (env.VITE_LANDS_ASSET_BASE ?? "").trim().replace(/\/+$/, "");
}

/** Families served as WebP rather than PNG: a threshold's glimpse is a 2450x1834 feathered piece,
 *  1.8-3.6 MB as PNG and ~200 KB as WebP at the same size (art, 2026-10-29; a palette PNG bands
 *  its alpha into rings). Every browser and WebView the app ships to decodes WebP. */
const WEBP_FAMILIES: ReadonlySet<string> = new Set(["glimpse", "travel"]);

export function assetUrl(id: string, scale: 1 | 2 = 2): string {
  const family = id.split(".", 1)[0];
  const group = id.startsWith("landmark.town.")
    ? "town"
    : (ASSET_FOLDERS[family] ?? family);
  return `${landsAssetBase()}/lands/${group}/${id}@${scale}x.${WEBP_FAMILIES.has(family) ? "webp" : "png"}`;
}

export interface ArtProps {
  id: string;
  tint?: string;
  w?: number | string;
  h?: number | string;
  ratio?: string;
  sketch?: boolean;
  round?: boolean;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
  hideId?: boolean;
  /** Tests / stories: skip the network attempt and always show the placeholder. */
  placeholderOnly?: boolean;
  /**
   * What VoiceOver says for the picture. Without it the art is decorative and out of the
   * accessibility tree: it used to be labelled with its asset id, so VoiceOver read
   * "scene.meadow.robot_walk, image" on the first spot card (13.11, 2026-10-22). Every
   * caller so far sits beside its own words (a name, a line), so none passes one yet.
   */
  alt?: string;
}

export function Art({
  id,
  tint = "sand",
  w,
  h,
  ratio,
  sketch = false,
  round = false,
  style,
  className = "",
  children,
  hideId = false,
  placeholderOnly = false,
  alt,
}: ArtProps) {
  const [failed, setFailed] = useState(false);
  const showImg = !placeholderOnly && !failed;
  return (
    <div
      className={`ld-art${sketch ? " sketch" : ""}${round ? " round" : ""}${showImg ? " has-img" : ""} ${className}`}
      {...(alt ? { role: "img", "aria-label": alt } : { "aria-hidden": true })}
      style={{ "--t": TINT[tint] ?? tint, width: w, height: h, aspectRatio: ratio, ...style } as CSSProperties}
    >
      {showImg && (
        <img
          src={assetUrl(id)}
          alt=""
          aria-hidden="true"
          draggable={false}
          onError={() => setFailed(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      )}
      {!hideId && !showImg && <span className="ld-art-id">{id}</span>}
      {children}
    </div>
  );
}
